/**
 * 10.9 — PORTAL DA ARENA (PvP) no Santuário. Identidade "arena": plataforma + 2 pilares + arco
 * com ESPADAS CRUZADAS grandes no topo + disco vermelho/laranja (cor de combate). O Caçador anda
 * até ele e interage pra abrir o lobby de PvP. Barato (poucos meshes), pulsa no update.
 * 10.9BP — base/runa/rótulo vêm de HubProps (compartilhado com o portal da Fenda).
 */
import * as THREE from "three";
import { addPortalLabel, makePortalBase } from "./HubProps";
import { crystalStdMat, metalMat } from "./Materials";
import { makeVortexMaterial, makePortalSparks, type VortexMaterial, type PortalSparks } from "./PortalVortex";
import { i18n } from "../i18n/i18n";

const PVP_COLOR = 0xff5a6a;

export class PvpPortal {
  readonly group = new THREE.Group();
  private readonly pos: THREE.Vector3;
  private ring!: THREE.Mesh;
  private disc!: THREE.Mesh;
  private swords!: THREE.Group;
  private portalMat!: VortexMaterial; // 10.9GU
  private sparks!: PortalSparks;
  private time = 0;

  constructor(scene: THREE.Scene, position: { x: number; z: number }) {
    this.pos = new THREE.Vector3(position.x, 0, position.z);
    this.buildMesh();
    this.group.position.copy(this.pos);
    scene.add(this.group);
  }

  get position(): THREE.Vector3 {
    return this.pos;
  }
  setVisible(v: boolean): void {
    this.group.visible = v;
  }
  isPlayerNear(playerPos: THREE.Vector3, radius = 2.8): boolean {
    return playerPos.distanceTo(this.pos) <= radius;
  }

  update(dt: number): void {
    this.time += dt;
    this.ring.rotation.y = this.time * 0.6;
    this.portalMat.uniforms.uTime.value = this.time;
    this.sparks.update(dt);
    // espadas no topo do arco: balanço leve (não flutuam mais no meio do vão)
    this.swords.rotation.y = Math.sin(this.time * 0.8) * 0.2;
    this.swords.position.y = 3.95 + Math.sin(this.time * 2.2) * 0.06;
  }

  private buildMesh(): void {
    // plataforma + runa (comum aos portais do hub)
    this.ring = makePortalBase(this.group, PVP_COLOR, 0.25).ring;

    // 2 pilares + arco — 10.9BQ: materiais da biblioteca (só o disco do vão é clone: pulsa)
    const pillarMat = crystalStdMat(0x3a2a34, 0x8a2a3a, 0.45);
    const pillarGeo = new THREE.CylinderGeometry(0.22, 0.28, 3.4, 6);
    const capMat = crystalStdMat(0xffb08a, PVP_COLOR, 1.1);
    for (const sx of [-1.15, 1.15]) {
      const p = new THREE.Mesh(pillarGeo, pillarMat);
      p.position.set(sx, 1.7, 0);
      p.castShadow = true;
      this.group.add(p);
      const cap = new THREE.Mesh(new THREE.OctahedronGeometry(0.26, 0), capMat);
      cap.position.set(sx, 3.5, 0);
      this.group.add(cap);
    }
    const arch = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.34, 0.34), pillarMat);
    arch.position.set(0, 3.45, 0);
    this.group.add(arch);

    // disco do portal (o "vão" mágico) — vertical, girando, aditivo, VERMELHO/LARANJA (combate)
    this.portalMat = makeVortexMaterial(PVP_COLOR, 1); // 10.9GU — vórtice
    this.disc = new THREE.Mesh(new THREE.CircleGeometry(1.05, 28), this.portalMat);
    this.disc.position.set(0, 1.85, 0);
    this.group.add(this.disc);
    this.sparks = makePortalSparks(this.group, PVP_COLOR, 1.6, 22, 4.2);
    const rim = new THREE.Mesh(new THREE.TorusGeometry(1.05, 0.08, 8, 28), crystalStdMat(0xffa040, 0xff8a3c, 1.3));
    rim.position.set(0, 1.85, 0);
    this.group.add(rim);

    // ESPADAS CRUZADAS grandes no TOPO do arco (motivo de duelo, legível de longe)
    this.swords = new THREE.Group();
    const bladeMat = metalMat(0xe8eeff);
    const bladeGeo = new THREE.BoxGeometry(0.12, 1.7, 0.04);
    for (const rot of [0.6, -0.6]) {
      const blade = new THREE.Mesh(bladeGeo, bladeMat);
      blade.rotation.z = rot;
      this.swords.add(blade);
      const guard = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.09, 0.07), bladeMat);
      guard.position.set(Math.sin(rot) * 0.6, -Math.cos(rot) * 0.6, 0);
      guard.rotation.z = rot;
      this.swords.add(guard);
    }
    this.swords.position.y = 3.95;
    this.group.add(this.swords);

    // 10.9B — rótulo flutuante
    addPortalLabel(this.group, i18n.t("pvp.interact"), PVP_COLOR, 5.4, i18n.t("pvp.portalSub"));
  }
}
